import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {view, Button, PropertyList, PropertyListItem} from '@sail/ui';
import {defineMessages, FormattedMessage} from 'react-intl';
import {Card, widgetLinkAction} from '@stripe-internal/conversations-ui';
import {CurrentMerchantSection} from '../helpers/CurrentMerchantSection';
import {CardForNewDesign} from '../helpers/CardForNewDesign';

export type ValueProps = {
  merchant_name: string;
  merchant_avatar: string;
  impacted_capability_summary: string;
  issue_summary: string;
  num_tasks: number;
  branch_issue_status: string;
  branch_verification_status: string;
  universal_help_new_design: boolean;
};

// These constants are used in the Universal Help tree and match the ones in Opus::Support::InlineData::MerchantVerificationStatus::MerchantVerificationStatus.
const REJECTED_STATUS = 'rejected';
const NO_ISSUES_STATUS = 'no_issues';
const MULTIPLE_ISSUES_STATUS = 'multiple_issues';
const SINGLE_ISSUE_STATUS = 'single_issue';

const ISSUE_IN_REVIEW_STATUS = 'in_review';
const ISSUE_IN_EXTERNAL_REVIEW_STATUS = 'in_external_review';
const ISSUE_ACTIONABLE_STATUS = 'actionable';
const ISSUE_NO_PERMISSION_STATUS = 'no_permission';

const CTA_LINK = 'https://dashboard.stripe.com/account/status';

const messages = defineMessages({
  isRejectedStatus: {
    id: 'mse_inline_data.universal_help_verification_rejected_status',
    description: 'Verification status copy for a rejected account',
    defaultMessage: 'We can no longer support your business',
  },
  isRejectedSummary: {
    id: 'mse_inline_data.universal_help_verification_rejected_summary',
    description: 'Issue summary copy for rejected account',
    defaultMessage:
      'After conducting a further review of your account we’ve determined that we will no longer be able to accept payments on your account moving forward.',
  },
  noActionRequiredSummary: {
    id: 'mse_inline_data.universal_help_verification_no_action_required_summary',
    description: 'Issue summary copy for no action required',
    defaultMessage:
      'Your account is verified. No further actions are necessary.',
  },
  reviewNumTasksCta: {
    id: 'mse_inline_data.cta_review_num_tasks',
    description: 'Number of tasks to review for CTA',
    defaultMessage: 'Review {nTasks} tasks',
  },
  actionRequiredStatus: {
    id: 'mse_inline_data.action_required_status',
    description: 'Status to show when the action required',
    defaultMessage: 'Action required',
  },
  reviewStatus: {
    id: 'mse_inline_data.is_review_status',
    description: 'Status to show when the merchant is in review',
    defaultMessage: 'In review',
  },
  noIssueStatus: {
    id: 'mse_inline_data.is_no_issue_status',
    description: 'Status to show when the merchant has no issues',
    defaultMessage: 'Verified',
  },
  externalReviewStatus: {
    id: 'mse_inline_data.is_external_review_status',
    description:
      'Status to show when the most important task is in external review',
    defaultMessage: 'Restrictions have been placed on this account',
  },
  externalReviewSummary: {
    id: 'mse_inline_data.is_external_review_summary',
    description:
      'Summary to show when the most important task is in external review',
    defaultMessage:
      'Please reach out to your platform administrator for more information',
  },
});

export const MerchantVerificationStatus = (
  props: InlineDatumReactComponentProps,
) => {
  const {datum} = props;

  const value = datum.value as ValueProps;
  const analytics = useAnalytics();

  const {
    merchant_name: merchantName,
    merchant_avatar: merchantAvatar,
    issue_summary: issueSummary,
    num_tasks: numTasks,
    branch_issue_status: branchIssueStatus,
    branch_verification_status: branchVerificationStatus,
    universal_help_new_design: universalHelpNewDesign,
  } = value;

  // Possible statuses (bolded text)
  // We only show a bolded header if we are
  // (1) rejected
  // (2) no_issues
  // (3) multiple issues
  const {heading, impactSummary} = (function (issueStatus, verificationStatus) {
    if (verificationStatus === REJECTED_STATUS) {
      return {
        heading: <FormattedMessage {...messages.isRejectedStatus} />,
        impactSummary: <FormattedMessage {...messages.isRejectedSummary} />,
      };
    }

    if (verificationStatus === NO_ISSUES_STATUS) {
      return {
        heading: <FormattedMessage {...messages.noIssueStatus} />,
        impactSummary: (
          <FormattedMessage {...messages.noActionRequiredSummary} />
        ),
      };
    }

    // The heading for single issue is shown in the next card's badge so we don't
    // duplicate here.
    if (verificationStatus === SINGLE_ISSUE_STATUS) {
      if (branchIssueStatus === ISSUE_IN_EXTERNAL_REVIEW_STATUS) {
        return {
          heading: null,
          impactSummary: (
            <FormattedMessage {...messages.externalReviewSummary} />
          ),
        };
      }
      return {
        heading: null,
        impactSummary: issueSummary,
      };
    }

    if (issueStatus === ISSUE_IN_EXTERNAL_REVIEW_STATUS) {
      return {
        heading: <FormattedMessage {...messages.externalReviewStatus} />,
        impactSummary: <FormattedMessage {...messages.externalReviewSummary} />,
      };
    }

    if (issueStatus === ISSUE_IN_REVIEW_STATUS) {
      return {
        heading: <FormattedMessage {...messages.reviewStatus} />,
        impactSummary: issueSummary,
      };
    }

    if (issueStatus === ISSUE_NO_PERMISSION_STATUS) {
      return {
        heading: <FormattedMessage {...messages.actionRequiredStatus} />,
        impactSummary: issueSummary,
      };
    }

    if (issueStatus === ISSUE_ACTIONABLE_STATUS) {
      return {
        heading: <FormattedMessage {...messages.actionRequiredStatus} />,
        impactSummary: issueSummary,
      };
    }

    return {
      heading: null,
      impactSummary: issueSummary,
    };
  })(branchIssueStatus, branchVerificationStatus);

  return (
    <CardForNewDesign
      universalHelpNewDesign={universalHelpNewDesign}
      CardComp={Card}
    >
      <view.div
        css={{gap: universalHelpNewDesign ? 'medium' : 'large', stack: 'y'}}
      >
        {!universalHelpNewDesign ? (
          <CurrentMerchantSection
            merchantName={merchantName}
            merchantAvatar={merchantAvatar}
          />
        ) : null}
        <PropertyList
          css={{
            gap: 'large',
            font: universalHelpNewDesign ? 'label.large' : 'label.medium',
          }}
        >
          <PropertyListItem
            label={heading}
            data-testid="verification-status-heading"
            css={{...(!universalHelpNewDesign && {gap: 'xsmall'})}}
            value={
              <view.span
                data-testid="impact-summary"
                // There are no sail variables that capture 12px && 16px line height
                style={{
                  ...(!universalHelpNewDesign && {
                    fontSize: heading ? '12px' : '14px',
                    lineHeight: heading ? '16px' : '20px',
                  }),
                }}
                css={{
                  ...(universalHelpNewDesign && {font: 'body.small'}),
                }}
              >
                {impactSummary}
              </view.span>
            }
            subviews={{
              value: {
                css: universalHelpNewDesign
                  ? {marginBottom: 'unset'}
                  : undefined,
              },
            }}
          />
        </PropertyList>
        {branchVerificationStatus === MULTIPLE_ISSUES_STATUS ? (
          <Button
            data-testid="cta-button"
            type={
              branchIssueStatus === ISSUE_IN_REVIEW_STATUS
                ? 'secondary'
                : 'primary'
            }
            css={{
              width: 'fill',
              ...(universalHelpNewDesign
                ? {font: 'label.medium.emphasized'}
                : {fontSize: 'label.small'}),
            }}
            uses={[widgetLinkAction()]}
            href={CTA_LINK}
            onPress={() => {
              analytics.action('uh_merchant_verification_status_button', {
                ctaLink: CTA_LINK,
                numTasks,
              });
            }}
          >
            <FormattedMessage
              {...messages.reviewNumTasksCta}
              values={{nTasks: numTasks}}
            />
          </Button>
        ) : null}
      </view.div>
    </CardForNewDesign>
  );
};
